import {
  AuthProvider,
  FranchiseModulesProvider,
  ThemeProvider,
  initI18n,
} from '@agos/shared'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { Route, Router, Switch } from 'wouter'

import { AppLayout, AuthGate } from './layout/AppLayout'
import { RouteGate } from './layout/RouteGate'
import { catchAllRoute, pathRoutes } from './module-registry'
import { ScaffoldNotice } from './ScaffoldNotice'
import { Toaster } from '@agos/ui'

initI18n()

/**
 * Query defaults copied from legacy `App.tsx`. They are not preferences: every
 * board's refresh behaviour is tuned against them, and `retry: false` is what
 * makes a failing kasa call surface at once rather than after four silent
 * attempts (§14.1).
 */
const queryClient = new QueryClient({
  defaultOptions: {
    queries: { refetchOnWindowFocus: false, retry: false },
  },
})

/**
 * The shell holds no feature code (§5): providers, the router assembly, the
 * layout chrome and the auth gate. Pages arrive from module route tables.
 *
 * Provider order is ported from legacy and matters — theme paints before auth
 * resolves so the login screen is already branded, and the module gate sits
 * inside auth because it is fetched per tenant.
 */
export function App() {
  const routes = pathRoutes()
  const notFound = catchAllRoute()
  const unauthenticatedRoutes = routes.filter((route) => route.requiresAuth === false)
  const inShell = routes.filter((route) => route.requiresAuth !== false && route.fullscreen !== true)
  // Behind the auth gate, outside the chrome: §9.6's driving surface is a phone in a driver's hand.
  const fullscreenRoutes = routes.filter(
    (route) => route.requiresAuth !== false && route.fullscreen === true,
  )

  return (
    <ThemeProvider>
      <AuthProvider>
        <FranchiseModulesProvider>
          <QueryClientProvider client={queryClient}>
            <Router base={import.meta.env.BASE_URL.replace(/\/$/, '')}>
              <Switch>
                {unauthenticatedRoutes.map((route) => (
                  <Route key={route.path} path={route.path} component={route.component} />
                ))}
                {fullscreenRoutes.map((route) => (
                  <Route key={route.path} path={route.path}>
                    <AuthGate>
                      <RouteGate guard={route.guard}>
                        <route.component />
                      </RouteGate>
                    </AuthGate>
                  </Route>
                ))}
                <Route>
                  <AuthGate>
                    <AppLayout>
                      <Switch>
                        {inShell.map((route) => (
                          <Route key={route.path} path={route.path}>
                            {/* The route's guard is enforced here, not just drawn in the sidebar. */}
                            <RouteGate guard={route.guard}>
                              <route.component />
                            </RouteGate>
                          </Route>
                        ))}
                        <Route component={notFound?.component ?? ScaffoldNotice} />
                      </Switch>
                    </AppLayout>
                  </AuthGate>
                </Route>
              </Switch>
            </Router>
            <Toaster />
          </QueryClientProvider>
        </FranchiseModulesProvider>
      </AuthProvider>
    </ThemeProvider>
  )
}
